import React, { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  LinearProgress,
  Chip,
  Button,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  Grid,
  Collapse,
  alpha
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccountBalance as BankIcon,
  CreditCard as CardIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Services
import { paymentService } from '../../services/paymentService';

// Constants
import { PAYMENT_METHODS, STRIPE_METHODS } from '../../constants/paymentConstants';

const DepositsHistoryInfinite = forwardRef(({ filters = {}, onError, disableAutoLoad = false }, ref) => {
  const { t } = useTranslation(['financial', 'common']);
  
  // États principaux - ARCHITECTURE HYBRIDE OPTIMISÉE
  const [allDeposits, setAllDeposits] = useState([]); // Cache en mémoire (limité)
  const [filteredDeposits, setFilteredDeposits] = useState([]); // Données filtrées côté frontend
  const [displayedDeposits, setDisplayedDeposits] = useState([]); // Données affichées avec pagination
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  // États pour le chargement backend par chunks
  const [backendPage, setBackendPage] = useState(1);
  const [hasMoreBackend, setHasMoreBackend] = useState(true);
  
  // États pour les devises et méthodes de paiement dynamiques
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState([]);
  
  // États pour les filtres et le tri
  const [localFilters, setLocalFilters] = useState({
    status: '',
    paymentMethod: '',
    currency: '',
    dateFrom: '',
    dateTo: '',
    ...filters
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [sortBy, setSortBy] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');

  // Refs pour la gestion du composant
  const sectionRef = useRef(null); // Ref pour la section des deposits
  const initialMountRef = useRef(true); // Ref pour tracker le premier montage

  // Configuration OPTIMISÉE pour performance
  const ITEMS_PER_PAGE = 5; // Affichage par page
  const BACKEND_CHUNK_SIZE = 50; // Taille raisonnable des chunks backend
  const MAX_MEMORY_ITEMS = 200; // Limite de sécurité mémoire
  const PRELOAD_THRESHOLD = 10; // Seuil pour précharger plus de données

  // 🚀 FILTRAGE OPTIMISÉ avec early returns
  const applyFiltersAndSort = useCallback((deposits, filters, sortBy, sortDirection) => {
    console.log('🔍 Applying OPTIMIZED filters:', { 
      totalDeposits: deposits.length, 
      filters, 
      sortBy, 
      sortDirection 
    });
    
    // ⚡ Performance: Si pas de filtres et tri par défaut, retourner directement
    const hasFilters = filters.status || filters.paymentMethod || filters.currency;
    if (!hasFilters && sortBy === 'date' && sortDirection === 'desc') {
      console.log('📈 No filters, returning original sorted list (FAST PATH)');
      return deposits;
    }
    
    // Étape 1: Filtrage avec optimisations
    let filtered = deposits;
    if (hasFilters) {
      filtered = deposits.filter(deposit => {
        // Early returns pour performance
        if (filters.status && filters.status !== '') {
          const depositStatus = deposit.status || deposit.deposit_status || 'held';
          if (depositStatus !== filters.status) return false;
        }
        
        if (filters.paymentMethod && filters.paymentMethod !== '') {
          const method = deposit.payment_method || deposit.method || deposit.paymentMethod || 'unknown';
          if (filters.paymentMethod === 'stripe' && !['stripe', 'card'].includes(method)) return false;
          if (filters.paymentMethod === 'cash' && method !== 'cash') return false;
        }
        
        if (filters.currency && filters.currency !== '') {
          const currency = deposit.currency || 'EUR';
          if (currency !== filters.currency) return false;
        }
        
        return true;
      });
    }
    
    // Étape 2: Tri optimisé
    if (sortBy) {
      filtered.sort((a, b) => {
        let valueA, valueB;
        
        switch (sortBy) {
          case 'date':
            valueA = new Date(a.created_at || a.date || a.createdAt || 0);
            valueB = new Date(b.created_at || b.date || b.createdAt || 0);
            break;
          case 'amount':
            valueA = parseFloat(a.amount || a.deposit_amount || 0);
            valueB = parseFloat(b.amount || b.deposit_amount || 0);
            break;
          case 'status':
            valueA = (a.status || a.deposit_status || 'held').toLowerCase();
            valueB = (b.status || b.deposit_status || 'held').toLowerCase();
            break;
          case 'paymentMethod':
            valueA = (a.payment_method || a.method || a.paymentMethod || 'unknown').toLowerCase();
            valueB = (b.payment_method || b.method || b.paymentMethod || 'unknown').toLowerCase();
            break;
          default:
            return 0;
        }
        
        if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
        if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    console.log(`📊 OPTIMIZED filtering: ${deposits.length} → ${filtered.length} deposits`);
    return filtered;
  }, []);

  // 📄 Pagination côté frontend
  const applyPagination = useCallback((deposits, page, itemsPerPage) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return deposits.slice(0, endIndex); // Pagination cumulative
  }, []);

  // 🔍 Extraction des valeurs uniques pour les filtres dynamiques
  const updateAvailableFilterOptions = useCallback((deposits) => {
    console.log('🔍 Updating available filter options from deposits...');
    
    // Extraire les devises uniques
    const currencies = [...new Set(
      deposits
        .map(deposit => deposit.currency || 'EUR')
        .filter(Boolean)
    )].sort();
    
    // Extraire les méthodes de paiement uniques
    const paymentMethods = [...new Set(
      deposits
        .map(deposit => deposit.payment_method || deposit.method || deposit.paymentMethod || 'unknown')
        .filter(method => method && method !== 'unknown')
    )].sort();
    
    console.log('💰 Available currencies:', currencies);
    console.log('💳 Available payment methods:', paymentMethods);
    
    setAvailableCurrencies(currencies);
    setAvailablePaymentMethods(paymentMethods);
  }, []);

  // 🔄 Chargement par chunks depuis le backend
  const loadDepositsChunk = useCallback(async (chunkPage = 1, reset = false) => {
    if (loading) {
      console.log('⚠️ Already loading, skipping chunk request');
      return { hasMore: false, newDeposits: [], totalFromBackend: 0 };
    }
    
    setLoading(true);
    if (reset) setError(null);
    
    try {
      console.log(`🔧 Loading CHUNK: page ${chunkPage}, size ${BACKEND_CHUNK_SIZE}`);
      
      const response = await paymentService.getRenterPaymentHistory({
        page: chunkPage,
        limit: BACKEND_CHUNK_SIZE,
        type: 'deposit',
        include_deposits: true,
        deposits_only: true,
        component: 'DepositsHistoryOptimized'
      });
      
      if (!response?.success) throw new Error(response?.error || 'Failed to load deposits');
      
      const allPayments = response.history || response.data?.history || response.data?.payments || [];
      
      // Traitement optimisé des données
      const processedDeposits = allPayments.map((item, index) => {
        const deposit = item.deposit || item;
        
        return {
          ...deposit,
          id: deposit.id || deposit._id || `deposit-${chunkPage}-${index}`,
          amount: deposit.amount || item.total_amount || 0,
          date: deposit.created_at || item.created_at || deposit.date || deposit.createdAt,
          status: deposit.status || 'held',
          paymentMethod: deposit.payment_method || 'unknown',
          currency: item.currency || deposit.currency || 'EUR',
          reservation_id: item.reservation_id || deposit.reservation_id,
          reservation_title: item.reservation_title,
          reservation_status: item.reservation_status
        };
      }).filter(deposit => deposit.id);
      
      const pagination = response.pagination || response.data?.pagination || {};
      const hasMoreChunks = Boolean(
        pagination.has_next_page || 
        pagination.hasNext || 
        processedDeposits.length === BACKEND_CHUNK_SIZE
      );
      
      console.log(`📊 Loaded CHUNK: ${processedDeposits.length} deposits, hasMore: ${hasMoreChunks}`);
      
      return { 
        hasMore: hasMoreChunks, 
        newDeposits: processedDeposits,
        totalFromBackend: pagination.total_items || pagination.totalItems || 0
      };
      
    } catch (error) {
      console.error('❌ Error loading chunk:', error);
      setError(error.message || t('financial:errors.depositsError', 'Erreur lors du chargement des dépôts'));
      if (onError) onError(error);
      return { hasMore: false, newDeposits: [], totalFromBackend: 0 };
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [onError]);

  // 📥 Chargement intelligent de plus de données backend
  const loadMoreFromBackend = useCallback(async () => {
    if (!hasMoreBackend || loading) {
      console.log('⚠️ Cannot load more from backend:', { hasMoreBackend, loading });
      return;
    }
    
    // 🛡️ Protection mémoire
    if (allDeposits.length >= MAX_MEMORY_ITEMS) {
      console.log(`⚠️ MEMORY LIMIT reached (${MAX_MEMORY_ITEMS}), stopping backend loading`);
      setHasMoreBackend(false);
      return;
    }
    
    const nextBackendPage = backendPage + 1;
    console.log(`📥 Loading MORE from backend: page ${nextBackendPage}`);
    
    const result = await loadDepositsChunk(nextBackendPage, false);
    
    if (result.newDeposits.length > 0) {
      setAllDeposits(prev => {
        const combined = [...prev, ...result.newDeposits];
        // Éliminer doublons et appliquer limite mémoire
        const unique = combined.filter((d, i, arr) => arr.findIndex(x => x.id === d.id) === i);
        const limited = unique.slice(0, MAX_MEMORY_ITEMS);
        
        console.log(`🔧 Backend merge: ${prev.length} + ${result.newDeposits.length} = ${limited.length} (limited)`);
        return limited;
      });
      setBackendPage(nextBackendPage);
    }
    
    setHasMoreBackend(result.hasMore);
    
    if (result.totalFromBackend) {
      setTotalItems(result.totalFromBackend);
    }
    
  }, [hasMoreBackend, loading, allDeposits.length, backendPage, loadDepositsChunk]);

  // 🚀 Chargement initial
  const loadInitialDeposits = useCallback(async () => {
    console.log('🚀 Loading INITIAL deposits chunk');
    
    // Reset tous les états
    setAllDeposits([]);
    setFilteredDeposits([]);
    setDisplayedDeposits([]);
    setBackendPage(1);
    setHasMoreBackend(true);
    setCurrentPage(1);
    setInitialLoading(true);
    
    const result = await loadDepositsChunk(1, true);
    
    if (result.newDeposits.length > 0) {
      setAllDeposits(result.newDeposits);
      setHasMoreBackend(result.hasMore);
      
      if (result.totalFromBackend) {
        setTotalItems(result.totalFromBackend);
      }
    }
  }, [loadDepositsChunk]);

  // 📄 Fonction pour afficher plus de résultats (pagination visuelle)
  const loadMoreDisplayed = useCallback(() => {
    console.log(`🔄 Load more DISPLAYED triggered. Current page: ${currentPage}`);
    
    const maxPage = Math.ceil(filteredDeposits.length / ITEMS_PER_PAGE);
    const isNearEnd = currentPage >= maxPage - 1;
    
    // 🔮 Préchargement intelligent
    if (isNearEnd && hasMoreBackend && filteredDeposits.length < MAX_MEMORY_ITEMS) {
      console.log('🔮 PRELOADING more data from backend...');
      loadMoreFromBackend();
    }
    
    if (currentPage >= maxPage) {
      console.log('ℹ️ No more filtered deposits to display');
      return;
    }
    
    const nextPage = currentPage + 1;
    console.log(`📥 Showing more DISPLAYED deposits (page ${nextPage})`);
    setCurrentPage(nextPage);
  }, [currentPage, filteredDeposits.length, hasMoreBackend, loadMoreFromBackend]);

  // 🔄 Effet pour appliquer les FILTRES seulement
  useEffect(() => {
    console.log('🔄 Applying FILTERS to cached deposits...');
    
    // Mettre à jour les options de filtres disponibles basées sur les vraies données
    updateAvailableFilterOptions(allDeposits);
    
    // Appliquer seulement les filtres, pas le tri
    const filtered = allDeposits.filter(deposit => {
      // Filtre par statut
      if (localFilters.status && localFilters.status !== '') {
        const depositStatus = deposit.status || deposit.deposit_status || 'held';
        if (depositStatus !== localFilters.status) return false;
      }
      
      // Filtre par méthode de paiement
      if (localFilters.paymentMethod && localFilters.paymentMethod !== '') {
        const method = deposit.payment_method || deposit.method || deposit.paymentMethod || 'unknown';
        // Filtrage exact par méthode de paiement réelle
        if (method !== localFilters.paymentMethod) return false;
      }
      
      // Filtre par devise
      if (localFilters.currency && localFilters.currency !== '') {
        const currency = deposit.currency || 'EUR';
        if (currency !== localFilters.currency) return false;
      }
      
      // Filtre par plage de dates
      if (localFilters.dateFrom || localFilters.dateTo) {
        const depositDate = new Date(deposit.created_at || deposit.date || deposit.createdAt);
        
        if (localFilters.dateFrom) {
          const fromDate = new Date(localFilters.dateFrom);
          fromDate.setHours(0, 0, 0, 0); // Début de journée
          if (depositDate < fromDate) return false;
        }
        
        if (localFilters.dateTo) {
          const toDate = new Date(localFilters.dateTo);
          toDate.setHours(23, 59, 59, 999); // Fin de journée
          if (depositDate > toDate) return false;
        }
      }
      
      return true;
    });
    
    setFilteredDeposits(filtered);
    setCurrentPage(1); // Reset pagination when filters change
  }, [allDeposits, localFilters, updateAvailableFilterOptions]);

  // 📄 Effet pour appliquer la pagination ET le tri sur les données filtrées
  useEffect(() => {
    console.log('🔄 Applying pagination AND sort to filtered deposits...');
    // D'abord appliquer la pagination pour garder toutes les données affichées
    const paginated = filteredDeposits.slice(0, currentPage * ITEMS_PER_PAGE);
    
    // Puis appliquer le tri sur les données paginées (déjà affichées)
    const sorted = [...paginated].sort((a, b) => {
      let valueA, valueB;
      
      switch (sortBy) {
        case 'date':
          valueA = new Date(a.created_at || a.date || a.createdAt || 0);
          valueB = new Date(b.created_at || b.date || b.createdAt || 0);
          break;
        case 'amount':
          valueA = parseFloat(a.amount || a.deposit_amount || 0);
          valueB = parseFloat(b.amount || b.deposit_amount || 0);
          break;
        case 'status':
          valueA = (a.status || a.deposit_status || 'held').toLowerCase();
          valueB = (b.status || b.deposit_status || 'held').toLowerCase();
          break;
        case 'paymentMethod':
          valueA = (a.payment_method || a.method || a.paymentMethod || 'unknown').toLowerCase();
          valueB = (b.payment_method || b.method || b.paymentMethod || 'unknown').toLowerCase();
          break;
        default:
          return 0;
      }
      
      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    setDisplayedDeposits(sorted);
    console.log(`📊 Sort applied: showing ${sorted.length} sorted deposits from ${paginated.length} paginated`);
  }, [filteredDeposits, currentPage, sortBy, sortDirection]);

  // 🚀 Chargement initial au montage - SEULEMENT AU PREMIER MONTAGE
  useEffect(() => {
    if (initialMountRef.current && !disableAutoLoad) {
      console.log('🚀 Initial component mount - loading first chunk');
      loadInitialDeposits();
      initialMountRef.current = false;
    } else if (disableAutoLoad) {
      console.log('🚀 Auto-load disabled, skipping initial load');
      initialMountRef.current = false;
    }
  }, []); // ✅ Dépendances vides pour ne déclencher qu'au premier montage

  // 🎯 Exposition de méthodes pour le composant parent
  useImperativeHandle(ref, () => ({
    loadData: () => {
      console.log('🎯 Manual load triggered from parent');
      loadInitialDeposits();
    },
    scrollToSection: () => {
      if (sectionRef.current) {
        sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }), [loadInitialDeposits]);

  // 🔧 Gestionnaires d'événements
  const handleFilterChange = (field, value) => {
    console.log(`🔧 Filter changed: ${field} = ${value}`);
    setLocalFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSort = (field) => {
    console.log(`🔧 Sort changed: ${field}`);
    if (sortBy === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('desc');
    }
  };

  const handleRefresh = () => {
    console.log('🔄 Manual refresh triggered');
    setError(null);
    loadInitialDeposits();
  };

  // 🎨 Fonctions utilitaires pour l'affichage
  const renderSortIcon = (field) => {
    if (sortBy !== field) return null;
    return sortDirection === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />;
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const getDepositStatusLabel = (status) => {
    const statusLabels = {
      'held': t('financial:deposit.status.held', 'Bloqué'),
      'released': t('financial:deposit.status.released', 'Libéré'),
      'partially_released': t('financial:deposit.status.partially_released', 'Partiellement libéré'),
      'pending': t('financial:deposit.status.pending', 'En attente'),
      'disputed': t('financial:deposit.status.disputed', 'Contesté'),
      'paid': t('financial:deposit.status.paid', 'Payé'),
      'cancelled': t('financial:deposit.status.cancelled', 'Annulé'),
      'refunded': t('financial:deposit.status.refunded', 'Remboursé')
    };
    return statusLabels[status] || status;
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'held': 'warning',
      'released': 'success',
      'partially_released': 'info',
      'pending': 'secondary',
      'disputed': 'error',
      'paid': 'success',
      'cancelled': 'error',
      'refunded': 'info'
    };
    return statusColors[status] || 'default';
  };
  
  const getMethodIcon = (method) => {
    if (STRIPE_METHODS.includes(method) || method === 'stripe' || method === 'card') {
      return <CardIcon fontSize="small" sx={{ mr: 0.5 }} />;
    }
    return <BankIcon fontSize="small" sx={{ mr: 0.5 }} />;
  };

  const getMethodText = (method) => {
    const methodKey = method?.toLowerCase?.() || method;
    
    // Mappage des valeurs de méthodes vers les clés de traduction
    const methodKeyMap = {
      'stripe': 'stripe',
      'card': 'card',
      'cash': 'cash',
      'virement': 'virement',
      'bank_transfer': 'bank_transfer',
      'unknown': 'other',
      null: 'other',
      undefined: 'other'
    };
    
    const translationKey = methodKeyMap[method] || methodKeyMap[methodKey] || 'other';
    return t(`financial:methods.${translationKey}`, method || '❓ Non défini');
  };

  return (
    <Box ref={sectionRef}>
      {/* En-tête avec statistiques */}
      <Box 
        sx={{ 
          mb: 3, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap'
        }}
      >
        <Typography variant="subtitle1" sx={{ mb: { xs: 2, sm: 0 } }}>
          {displayedDeposits.length > 0 ? (
            <>
              {displayedDeposits.length} sur {filteredDeposits.length} {t('financial:deposit.title', 'Dépôt(s)')}
              {filteredDeposits.length !== totalItems && ` (${totalItems} au total)`}
              {allDeposits.length >= MAX_MEMORY_ITEMS && ` [Cache: ${allDeposits.length}]`}
            </>
          ) : loading ? (
            <>{t('financial:deposit.loading', 'Chargement des dépôts...')}</>
          ) : (
            <>{t('financial:deposit.empty', 'Aucun dépôt trouvé')}</>
          )}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<FilterIcon />}
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            size="small"
          >
            {isFilterOpen ? t('financial:deposit.hideFilters', 'Masquer les filtres') : t('financial:deposit.showFilters', 'Filtrer')}
          </Button>
          
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<SortIcon />}
            size="small"
            onClick={() => handleSort(sortBy === 'date' ? 'amount' : 'date')}
          >
            {sortBy === 'date' ? 'Trier par montant' : 'Trier par date'}
          </Button>
        </Box>
      </Box>

      {/* Filtres avancés */}
      <Collapse in={isFilterOpen}>
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="status-filter-label">{t('financial:deposit.methodLabels.statusLabel', 'Statut')}</InputLabel>
                <Select
                  labelId="status-filter-label"
                  id="status-filter"
                  value={localFilters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label={t('financial:deposit.methodLabels.statusLabel', 'Statut')}
                >
                  <MenuItem value="">{t('financial:deposit.filters.allStatuses', 'Tous les statuts')}</MenuItem>
                  <MenuItem value="held">{t('financial:deposit.status.held', 'Bloqué')}</MenuItem>
                  <MenuItem value="released">{t('financial:deposit.status.released', 'Libéré')}</MenuItem>
                  <MenuItem value="partially_released">{t('financial:deposit.status.partially_released', 'Partiellement libéré')}</MenuItem>
                  <MenuItem value="pending">{t('financial:deposit.status.pending', 'En attente')}</MenuItem>
                  <MenuItem value="disputed">{t('financial:deposit.status.disputed', 'Contesté')}</MenuItem>
                  <MenuItem value="cancelled">{t('financial:deposit.status.cancelled', 'Annulé')}</MenuItem>
                  <MenuItem value="refunded">{t('financial:deposit.status.refunded', 'Remboursé')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel id="payment-method-filter-label">{t('financial:deposit.methodLabels.paymentMethodLabel', 'Méthode de paiement')}</InputLabel>
                <Select
                  labelId="payment-method-filter-label"
                  id="payment-method-filter"
                  value={localFilters.paymentMethod}
                  onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                  label={t('financial:deposit.methodLabels.paymentMethodLabel', 'Méthode de paiement')}
                >
                  <MenuItem value="">{t('financial:deposit.filters.allMethods', 'Toutes les méthodes')}</MenuItem>
                  {availablePaymentMethods.map(method => (
                    <MenuItem key={method} value={method}>
                      {method === 'stripe' || method === 'card' ? t('financial:deposit.methodLabels.onlinePayment', 'Paiement en ligne') :
                       method === 'cash' ? t('financial:deposit.methodLabels.cashPayment', 'Paiement en espèces') :
                       method === 'virement' || method === 'bank_transfer' ? t('financial:deposit.methodLabels.bankTransfer', 'Virement bancaire') :
                       method.charAt(0).toUpperCase() + method.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={6} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="currency-filter-label">{t('financial:deposit.methodLabels.currencyLabel', 'Devise')}</InputLabel>
                <Select
                  labelId="currency-filter-label"
                  id="currency-filter"
                  value={localFilters.currency}
                  onChange={(e) => handleFilterChange('currency', e.target.value)}
                  label={t('financial:deposit.methodLabels.currencyLabel', 'Devise')}
                >
                  <MenuItem value="">{t('financial:deposit.filters.allCurrencies', 'Toutes')}</MenuItem>
                  {availableCurrencies.map(currency => (
                    <MenuItem key={currency} value={currency}>
                      {currency}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Filtres de plage de dates */}
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={t('financial:deposit.filters.from', 'Du')}
                value={localFilters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputLabel-root': { fontSize: '0.875rem' } }}
              />
            </Grid>
            
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label={t('financial:deposit.filters.to', 'Au')}
                value={localFilters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ '& .MuiInputLabel-root': { fontSize: '0.875rem' } }}
              />
            </Grid>
          </Grid>
          
          {/* Boutons d'actions pour les filtres */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={() => {
                setLocalFilters({
                  status: '',
                  paymentMethod: '',
                  currency: '',
                  dateFrom: '',
                  dateTo: ''
                });
              }}
            >
              {t('financial:deposit.quickFilters.clearFilters', 'Effacer les filtres')}
            </Button>
            
            {/* Raccourcis de plages de dates */}
            <Button
              size="small"
              variant="text"
              onClick={() => {
                const today = new Date();
                const sevenDaysAgo = new Date(today);
                sevenDaysAgo.setDate(today.getDate() - 7);
                
                setLocalFilters(prev => ({
                  ...prev,
                  dateFrom: sevenDaysAgo.toISOString().split('T')[0],
                  dateTo: today.toISOString().split('T')[0]
                }));
              }}
            >
              {t('financial:deposit.quickFilters.last7Days', '7 derniers jours')}
            </Button>
            
            <Button
              size="small"
              variant="text"
              onClick={() => {
                const today = new Date();
                const thirtyDaysAgo = new Date(today);
                thirtyDaysAgo.setDate(today.getDate() - 30);
                
                setLocalFilters(prev => ({
                  ...prev,
                  dateFrom: thirtyDaysAgo.toISOString().split('T')[0],
                  dateTo: today.toISOString().split('T')[0]
                }));
              }}
            >
              {t('financial:deposit.quickFilters.last30Days', '30 derniers jours')}
            </Button>
            
            <Button
              size="small"
              variant="text"
              onClick={() => {
                const today = new Date();
                const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                
                setLocalFilters(prev => ({
                  ...prev,
                  dateFrom: firstDayOfMonth.toISOString().split('T')[0],
                  dateTo: today.toISOString().split('T')[0]
                }));
              }}
            >
              {t('financial:deposit.quickFilters.thisMonth', 'Ce mois')}
            </Button>
          </Box>
        </Paper>
      </Collapse>

      {/* Tableau des dépôts */}
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell 
                onClick={() => handleSort('date')}
                sx={{ 
                  cursor: 'pointer', 
                  '&:hover': { bgcolor: alpha('#000', 0.05) },
                  fontWeight: 'bold'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {t('financial:deposit.table.date', 'Date')} {renderSortIcon('date')}
                </Box>
              </TableCell>
              <TableCell 
                onClick={() => handleSort('status')}
                sx={{ 
                  cursor: 'pointer', 
                  '&:hover': { bgcolor: alpha('#000', 0.05) },
                  fontWeight: 'bold'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {t('financial:deposit.table.status', 'Statut')} {renderSortIcon('status')}
                </Box>
              </TableCell>
              <TableCell 
                onClick={() => handleSort('paymentMethod')}
                sx={{ 
                  cursor: 'pointer', 
                  '&:hover': { bgcolor: alpha('#000', 0.05) },
                  fontWeight: 'bold'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {t('financial:deposit.table.method', 'Méthode')} {renderSortIcon('paymentMethod')}
                </Box>
              </TableCell>
              <TableCell 
                onClick={() => handleSort('amount')}
                sx={{ 
                  cursor: 'pointer', 
                  '&:hover': { bgcolor: alpha('#000', 0.05) },
                  fontWeight: 'bold'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {t('financial:deposit.table.amount', 'Montant')} {renderSortIcon('amount')}
                </Box>
              </TableCell>
              <TableCell>{t('financial:deposit.table.reservation', 'Réservation')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedDeposits.length === 0 && !loading && !initialLoading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    {localFilters.status || localFilters.paymentMethod || localFilters.currency || localFilters.dateFrom || localFilters.dateTo
                      ? t('financial:deposit.noFiltersMatch', 'Aucun dépôt ne correspond aux filtres sélectionnés')
                      : t('financial:deposit.empty', 'Aucun dépôt trouvé')
                    }
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayedDeposits.map((deposit, index) => (
                <TableRow 
                  key={deposit.id || deposit._id || `deposit-${index}`} 
                  sx={{ 
                    '&:hover': { bgcolor: alpha('#000', 0.02) },
                    cursor: 'pointer'
                  }}
                >
                  <TableCell>
                    {formatDate(deposit.created_at || deposit.date || deposit.createdAt || new Date())}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getDepositStatusLabel(deposit.deposit_status || deposit.status || 'held')}
                      color={getStatusColor(deposit.deposit_status || deposit.status || 'held')}
                      size="small"
                      sx={{ fontWeight: 500 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getMethodIcon(deposit.payment_method || deposit.method || deposit.paymentMethod)}
                      {getMethodText(deposit.payment_method || deposit.method || deposit.paymentMethod)}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(
                        deposit.amount || 
                        deposit.deposit_amount || 
                        deposit.depositAmount || 
                        deposit.total || 
                        100,
                        deposit.currency || 'EUR'
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={`Voir les détails de la réservation #${deposit.reservation_id || deposit.reservationId || deposit.reservation?.id || deposit._id || 'ID'}`}>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        color="primary"
                        sx={{ borderRadius: 4, py: 0.5 }}
                      >
                        #{deposit.reservation_id || deposit.reservationId || (deposit.reservation && deposit.reservation.id) || deposit._id || 'ID'}
                      </Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {(loading || initialLoading) && (
          <Box sx={{ p: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
              {initialLoading ? t('financial:deposit.loadingInitial', 'Chargement initial...') : t('financial:deposit.loadingMore', 'Chargement de plus de dépôts...')}
            </Typography>
          </Box>
        )}
        {error && (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography color="error" sx={{ mb: 1 }}>{error}</Typography>
            <Button 
              variant="outlined" 
              color="primary" 
              onClick={handleRefresh}
              sx={{ mt: 1 }}
            >
              {t('common:retry', 'Réessayer')}
            </Button>
          </Box>
        )}
      </TableContainer>
      
      {/* Section des boutons d'action */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2, gap: 2 }}>
        {/* Bouton "Load More" - Affiche plus de résultats filtrés */}
        {displayedDeposits.length < filteredDeposits.length && !loading && !initialLoading && (
          <Button
            variant="contained"
            color="primary"
            onClick={loadMoreDisplayed}
            startIcon={<ExpandMoreIcon />}
            sx={{ borderRadius: 2, px: 3 }}
            disabled={loading}
          >
            {t('financial:deposit.loadMore', 'Charger plus de dépôts')} ({displayedDeposits.length}/{filteredDeposits.length})
          </Button>
        )}
        
        {/* Bouton pour charger plus de données backend */}
        {displayedDeposits.length >= filteredDeposits.length && hasMoreBackend && allDeposits.length < MAX_MEMORY_ITEMS && !loading && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={loadMoreFromBackend}
            startIcon={<ExpandMoreIcon />}
            sx={{ borderRadius: 2, px: 3 }}
            disabled={loading}
          >
            {`Charger plus depuis le serveur (${allDeposits.length}/${totalItems})`}
          </Button>
        )}
        
        {/* Message si tous les dépôts filtrés sont affichés */}
        {displayedDeposits.length > 0 && displayedDeposits.length >= filteredDeposits.length && !hasMoreBackend && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            ✅ {t('financial:deposit.messages.allDisplayed', 'Tous les dépôts correspondants sont affichés')} ({displayedDeposits.length}/{filteredDeposits.length})
          </Typography>
        )}
        
        {/* Bouton "Refresh" */}
        <Button
          variant="outlined"
          color="primary"
          onClick={handleRefresh}
          startIcon={<RefreshIcon />}
          sx={{ borderRadius: 2, px: 3 }}
          disabled={loading || initialLoading}
        >
          {t('common:refresh', 'Actualiser')}
        </Button>
      </Box>
      
      {/* Informations de statut OPTIMISÉES */}
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {displayedDeposits.length > 0 ? (
            <>
              📊 {displayedDeposits.length} {t('financial:deposit.pagination.showing')} {filteredDeposits.length} {t('financial:deposit.pagination.matching')}
              {filteredDeposits.length !== totalItems && ` (${totalItems} ${t('financial:deposit.pagination.total')})`}
              {allDeposits.length >= MAX_MEMORY_ITEMS && ` 💾 ${t('financial:deposit.pagination.cacheLimit')} ${MAX_MEMORY_ITEMS}`}
            </>
          ) : loading || initialLoading ? (
            t('financial:deposit.pagination.loadingInProgress')
          ) : (
            t('financial:deposit.pagination.notFoundWithFilters')
          )}
        </Typography>
        
        {/* Info performance */}
        <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 1 }}>
          ⚡ {t('financial:deposit.performance.cache')}: {allDeposits.length} {t('financial:deposit.performance.deposits')} | 🔍 {t('financial:deposit.performance.filtered')}: {filteredDeposits.length} | 📄 {t('financial:deposit.performance.displayed')}: {displayedDeposits.length}
          {hasMoreBackend && ` | 📡 ${t('financial:deposit.performance.moreDataAvailable')}`}
          {sortBy !== 'date' && ` | 🔄 ${t('financial:deposit.performance.sort')}: ${sortBy} ${sortDirection}`}
        </Typography>
        
        {/* Info filtres actifs */}
        {(localFilters.status || localFilters.paymentMethod || localFilters.currency || localFilters.dateFrom || localFilters.dateTo) && (
          <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
            🔧 {t('financial:deposit.activeFilters.title')}: {[
              localFilters.status && `${t('financial:deposit.activeFilters.status')}: ${localFilters.status}`,
              localFilters.paymentMethod && `${t('financial:deposit.activeFilters.method')}: ${localFilters.paymentMethod}`,
              localFilters.currency && `${t('financial:deposit.activeFilters.currency')}: ${localFilters.currency}`,
              localFilters.dateFrom && `${t('financial:deposit.activeFilters.from')}: ${localFilters.dateFrom}`,
              localFilters.dateTo && `${t('financial:deposit.activeFilters.to')}: ${localFilters.dateTo}`
            ].filter(Boolean).join(', ')}
          </Typography>
        )}
      </Box>
    </Box>
  );
});

export default DepositsHistoryInfinite;
